import React, { useState } from 'react';
import PageSEO from '../components/PageSEO';
import GalleryLightbox, { ImageLightboxModal, ClickableWorkImage } from '../components/GalleryLightbox';
import WebContactForm from '../components/WebContactForm';
import { SITE_INFO, BRAND_ASSETS } from '../data';

export default function MaterialerPage() {
  const [lightboxState, setLightboxState] = useState({ isOpen: false, index: 0 });

  const materialImages = [
    {
      src: "https://assets.auxodev.dk/assets/e25852aa-8434-4aa0-ac76-be91340b9170/materialer-maskinarbejde.webp",
      alt: "Maskiner håndterer materialer på KTS-Entreprise plads",
      caption: "Maskiner håndterer materialer på KTS-Entreprise plads"
    },
    {
      src: "https://assets.auxodev.dk/assets/e25852aa-8434-4aa0-ac76-be91340b9170/materialer-muldjord.webp",
      alt: "Muldjord på KTS-Entreprise materialeplads",
      caption: "Muldjord på KTS-Entreprise materialeplads"
    },
    {
      src: "https://assets.auxodev.dk/assets/e25852aa-8434-4aa0-ac76-be91340b9170/materialer-knuste-materialer.webp",
      alt: "Bunke af knuste materialer på KTS-Entreprise plads",
      caption: "Bunke af knuste materialer på KTS-Entreprise plads"
    },
    {
      src: "https://assets.auxodev.dk/assets/e25852aa-8434-4aa0-ac76-be91340b9170/materialer-betonbrokker.webp",
      alt: "Betonbrokker til modtagelse og genanvendelse",
      caption: "Betonbrokker til modtagelse og genanvendelse"
    }
  ];

  const materialsList = [
    { name: 'Brolæggergrus', type: 'Grus og sand' },
    { name: 'Stabilgrus', type: 'Grus og sand' },
    { name: 'Ærtesten', type: 'Stenmaterialer' },
    { name: 'Nøddesten', type: 'Stenmaterialer' },
    { name: 'Mure- og pudsegrus', type: 'Grus og sand' },
    { name: 'Støbemix', type: 'Grus og sand' },
    { name: 'Harpet muldjord', type: 'Jordmaterialer' },
    { name: 'Harpet sandfyld', type: 'Jordmaterialer' },
    { name: 'Knust tegl og beton', type: 'Genbrugsmaterialer' },
    { name: 'Knust beton', type: 'Genbrugsmaterialer' },
    { name: 'Knust asfalt', type: 'Genbrugsmaterialer' }
  ];

  const acceptedMaterials = [
    'Ren beton',
    'Rene mursten',
    'Uglaseret tegl',
    'Rene blandinger af beton, mursten og uglaseret tegl',
    'Asfalt',
    'Asfalt/beton'
  ];

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="bg-stone-100 min-h-screen">
      <PageSEO
        title="Salg af materialer og modtagelse af rent bygge- og anlægsaffald | KTS-Entreprise A/S"
        description="På vores plads på Industrivej 3 i Frederiksværk sælger vi grus, sand, muld og genbrugsmaterialer til private og erhverv. Vi modtager desuden udvalgte typer rene og sorterede bygge- og anlægsmaterialer."
        ogTitle="Salg af materialer og modtagelse af rent bygge- og anlægsaffald | KTS-Entreprise A/S"
        ogDescription="På vores plads på Industrivej 3 i Frederiksværk sælger vi grus, sand, muld og genbrugsmaterialer til private og erhverv. Vi modtager desuden udvalgte typer rene og sorterede bygge- og anlægsmaterialer."
        ogImage={BRAND_ASSETS.defaultOgImage}
        slug="bygningsaffald"
      />

      {/* Header Banner */}
      <section className="bg-stone-900 text-white pt-36 sm:pt-44 md:pt-48 lg:pt-52 pb-12 sm:pb-16 border-b-4 border-[#0072c0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#0072c0]/20 border border-[#0072c0]/40 text-blue-200 text-xs font-heading font-bold uppercase tracking-wider mb-3 rounded-xs">
            <span>På pladsen</span>
          </div>
          <h1 className="text-3xl sm:text-5xl font-black uppercase tracking-tight text-white font-heading">
            Salg af materialer og modtagelse af rent bygge- og anlægsaffald
          </h1>
          <p className="text-stone-300 text-base sm:text-lg mt-4 max-w-3xl leading-relaxed">
            På vores plads på Industrivej 3 i Frederiksværk sælger vi grus, sand, muld og genbrugsmaterialer til private og erhverv. Vi modtager desuden udvalgte typer rene og sorterede bygge- og anlægsmaterialer.
          </p>

          {/* Quick jump anchor buttons */}
          <div className="flex flex-wrap gap-4 mt-8">
            <button
              onClick={() => scrollToSection('materialer-til-salg')}
              className="inline-flex items-center gap-2.5 bg-[#0072c0] hover:bg-[#005b99] active:bg-[#00487a] text-white font-heading font-bold uppercase tracking-wider px-6 py-3 text-xs sm:text-sm transition-all duration-200 shadow-md cursor-pointer rounded-xs"
            >
              <span>Se materialer til salg</span>
              <i className="fa-solid fa-arrow-down text-xs" aria-hidden="true"></i>
            </button>

            <button
              onClick={() => scrollToSection('modtagelse-af-rene-materialer')}
              className="inline-flex items-center gap-2.5 bg-stone-800 hover:bg-stone-700 text-white font-heading font-bold uppercase tracking-wider px-6 py-3 text-xs sm:text-sm transition-all duration-200 border border-stone-700 shadow-md cursor-pointer rounded-xs"
            >
              <span>Se, hvad vi modtager</span>
              <i className="fa-solid fa-arrow-down text-xs" aria-hidden="true"></i>
            </button>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16">
        
        {/* Tydeligt hovedbillede for pladsen */}
        <div className="mb-12 border border-stone-200 bg-white p-2 rounded-xs shadow-xs overflow-hidden">
          <ClickableWorkImage
            src={materialImages[0].src}
            alt={materialImages[0].alt}
            className="w-full h-auto max-h-[460px] object-cover rounded-xs"
            priority={true}
            onClick={() => setLightboxState({ isOpen: true, index: 0 })}
          />
        </div>
        
        {/* Section 1: Materialer til salg */}
        <section
          id="materialer-til-salg"
          className="bg-white border border-stone-200 p-6 sm:p-10 rounded-xs shadow-xs mb-12 scroll-mt-28"
        >
          <div className="border-l-4 border-[#0072c0] pl-4 mb-6">
            <h2 className="text-2xl sm:text-3xl font-extrabold uppercase tracking-tight text-stone-900 font-heading">
              Materialer til salg
            </h2>
          </div>

          <p className="text-stone-700 text-sm sm:text-base leading-relaxed mb-6 font-medium">
            Her vises de materialer, som kan købes og afhentes på pladsen:
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mb-8">
            {materialsList.map((item, idx) => (
              <div
                key={idx}
                className="bg-stone-50 border border-stone-200 p-4 rounded-xs flex items-center gap-3 hover:border-[#0072c0]/50 transition-colors"
              >
                <div className="w-8 h-8 rounded-full bg-[#0072c0]/10 text-[#0072c0] flex items-center justify-center shrink-0">
                  <i className="fa-solid fa-cubes text-xs"></i>
                </div>
                <div>
                  <span className="text-sm font-bold text-stone-900 block font-heading">
                    {item.name}
                  </span>
                  <span className="text-[11px] text-stone-500 uppercase font-semibold">
                    {item.type}
                  </span>
                </div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-6">
            <div className="border border-stone-200 bg-white p-2 rounded-xs shadow-xs overflow-hidden flex flex-col justify-between">
              <ClickableWorkImage
                src={materialImages[1].src}
                alt={materialImages[1].alt}
                className="w-full h-64 sm:h-72 object-cover rounded-xs"
                style={{ objectPosition: 'center 40%' }}
                onClick={() => setLightboxState({ isOpen: true, index: 1 })}
              />
              <div className="pt-2 px-1 flex items-center justify-between text-xs font-heading font-bold text-stone-700 uppercase">
                <span>Muldjord</span>
                <span className="text-stone-400 font-normal">Salg og afhentning</span>
              </div>
            </div>

            <div className="border border-stone-200 bg-white p-2 rounded-xs shadow-xs overflow-hidden flex flex-col justify-between">
              <ClickableWorkImage
                src={materialImages[2].src}
                alt={materialImages[2].alt}
                className="w-full h-64 sm:h-72 object-cover rounded-xs"
                onClick={() => setLightboxState({ isOpen: true, index: 2 })}
              />
              <div className="pt-2 px-1 flex items-center justify-between text-xs font-heading font-bold text-stone-700 uppercase">
                <span>Knuste materialer</span>
                <span className="text-stone-400 font-normal">Salg og afhentning</span>
              </div>
            </div>
          </div>

          <div className="p-4 sm:p-5 bg-stone-50 border-l-4 border-[#0072c0] rounded-xs text-stone-800 text-sm sm:text-base leading-relaxed">
            <p className="font-semibold text-stone-900">
              Kontakt os gerne for evt. afhentning eller for oplysninger om aktuelle priser og lagerstatus.
            </p>
          </div>
        </section>

        {/* Section 2: Modtagelse af rene materialer */}
        <section
          id="modtagelse-af-rene-materialer"
          className="bg-white border border-stone-200 p-6 sm:p-10 rounded-xs shadow-xs mb-12 scroll-mt-28"
        >
          <div className="border-l-4 border-[#0072c0] pl-4 mb-6">
            <h2 className="text-2xl sm:text-3xl font-extrabold uppercase tracking-tight text-stone-900 font-heading">
              Modtagelse af rene materialer
            </h2>
          </div>

          {/* Vigtig bemærkning: Midlertidigt stop for modtagelse af asfalt */}
          <div className="mb-6 p-4 sm:p-5 bg-amber-50 border-l-4 border-amber-500 rounded-xs text-stone-900 shadow-xs">
            <div className="flex items-start gap-3 sm:gap-4">
              <div className="w-8 h-8 rounded-full bg-amber-200/90 text-amber-800 flex items-center justify-center shrink-0 mt-0.5">
                <i className="fa-solid fa-triangle-exclamation text-sm" aria-hidden="true"></i>
              </div>
              <div className="space-y-1.5">
                <h3 className="font-heading font-extrabold text-base sm:text-lg uppercase tracking-tight text-amber-950">
                  Vi har midlertidigt stop for modtagelse af asfalt
                </h3>
                <p className="text-stone-800 text-sm sm:text-base leading-relaxed">
                  Vi oplyser her på siden, når vi igen åbner for modtagelse af asfalt.
                </p>
                <p className="text-stone-800 text-sm sm:text-base leading-relaxed">
                  Du er også velkommen til at ringe til os og høre om status på tlf.{' '}
                  <a
                    href={`tel:${SITE_INFO.phoneClean}`}
                    className="font-bold text-[#0072c0] hover:underline whitespace-nowrap"
                  >
                    {SITE_INFO.phone}
                  </a>.
                </p>
              </div>
            </div>
          </div>

          <p className="text-stone-700 text-sm sm:text-base leading-relaxed mb-6">
            Vi modtager udvalgte typer rene og sorterede læs fra bygge- og anlægsopgaver.
          </p>

          <div className="mb-6">
            <h3 className="text-sm sm:text-base font-heading font-bold uppercase text-stone-900 mb-3">
              Vi modtager:
            </h3>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {acceptedMaterials.map((mat, idx) => {
                const isAsphalt = mat.toLowerCase().includes('asfalt');
                return (
                  <li
                    key={idx}
                    className={`flex items-center justify-between gap-3 p-3 border rounded-xs text-sm font-medium ${
                      isAsphalt
                        ? 'bg-amber-50/70 border-amber-200 text-stone-700'
                        : 'bg-stone-50 border-stone-200 text-stone-900'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      {isAsphalt ? (
                        <i className="fa-solid fa-circle-pause text-amber-600 text-sm shrink-0" aria-hidden="true"></i>
                      ) : (
                        <i className="fa-solid fa-check text-emerald-600 text-sm shrink-0" aria-hidden="true"></i>
                      )}
                      <span className={isAsphalt ? 'line-through decoration-amber-500 text-stone-600' : ''}>
                        {mat}
                      </span>
                    </div>
                    {isAsphalt && (
                      <span className="text-[11px] font-heading font-bold uppercase tracking-wider text-amber-900 bg-amber-100/90 border border-amber-300/80 px-2 py-0.5 rounded-xs shrink-0">
                        Midlertidigt stop
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>

          <div className="my-6 border border-stone-200 bg-white p-2 rounded-xs shadow-xs overflow-hidden">
            <ClickableWorkImage
              src={materialImages[3].src}
              alt={materialImages[3].alt}
              className="w-full h-auto max-h-[380px] object-cover rounded-xs"
              onClick={() => setLightboxState({ isOpen: true, index: 3 })}
            />
          </div>

          <div className="space-y-4">
            <div className="p-4 sm:p-5 bg-amber-50 border border-amber-200 rounded-xs text-stone-800 text-xs sm:text-sm leading-relaxed">
              <strong className="block font-heading uppercase text-stone-900 mb-1">
                Krav til renhed:
              </strong>
              <p>
                Materialerne må ikke indeholde jord, gips, gasbeton, isolering, træ, plast, tagpap, kabler, glas, rødder eller andre fremmedmaterialer.
              </p>
            </div>

            <div className="p-4 sm:p-5 bg-stone-50 border border-stone-200 rounded-xs text-stone-700 text-xs sm:text-sm leading-relaxed">
              <p>
                Urene eller fejlsorterede læs vil blive afvist og anvist til en anden godkendt modtager.
              </p>
            </div>
          </div>
        </section>

        {/* Section 3: Fælles information nederst på 'På pladsen' */}
        <section className="bg-white border border-stone-200 p-6 sm:p-8 rounded-xs shadow-xs mb-16">
          <div className="border-l-4 border-[#0072c0] pl-4 mb-6">
            <h2 className="text-xl sm:text-2xl font-extrabold uppercase tracking-tight text-stone-900 font-heading">
              Praktisk information og åbningstider
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="flex items-start gap-3 p-4 bg-stone-50 border border-stone-200 rounded-xs">
              <div className="w-10 h-10 rounded-full bg-[#0072c0]/10 text-[#0072c0] flex items-center justify-center shrink-0">
                <i className="fa-solid fa-location-dot"></i>
              </div>
              <div>
                <span className="text-xs font-bold uppercase text-stone-500 block font-heading">Adresse</span>
                <p className="text-sm font-bold text-stone-900">Industrivej 3, 3300 Frederiksværk</p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-4 bg-stone-50 border border-stone-200 rounded-xs">
              <div className="w-10 h-10 rounded-full bg-[#0072c0]/10 text-[#0072c0] flex items-center justify-center shrink-0">
                <i className="fa-solid fa-clock"></i>
              </div>
              <div>
                <span className="text-xs font-bold uppercase text-stone-500 block font-heading">Åbningstider</span>
                <p className="text-xs sm:text-sm text-stone-800">
                  <strong>Mandag–torsdag:</strong> kl. 6.30–15.30<br />
                  <strong>Fredag:</strong> kl. 6.30–15.00
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-4 bg-stone-50 border border-stone-200 rounded-xs">
              <div className="w-10 h-10 rounded-full bg-[#0072c0]/10 text-[#0072c0] flex items-center justify-center shrink-0">
                <i className="fa-solid fa-wallet"></i>
              </div>
              <div>
                <span className="text-xs font-bold uppercase text-stone-500 block font-heading">Privat salg</span>
                <p className="text-xs sm:text-sm text-stone-800">
                  Kontant betaling eller MobilePay
                </p>
              </div>
            </div>
          </div>

          {/* Miljøgodkendelse link */}
          <div className="pt-4 border-t border-stone-200 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <i className="fa-solid fa-file-shield text-[#0072c0]" aria-hidden="true"></i>
              <span className="text-xs sm:text-sm text-stone-700">
                Pladsen er miljøgodkendt til modtagelse og oparbejdning af rene bygge- og anlægsmaterialer.
              </span>
            </div>
            <a
              href="https://dma.mst.dk/vis-virksomhed/093f7bda-d202-4092-aeab-a86e56e64829"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-xs sm:text-sm font-heading font-bold uppercase tracking-wider text-[#0072c0] hover:text-[#005b99] hover:underline"
            >
              <span>Se miljøgodkendelse</span>
              <i className="fa-solid fa-arrow-up-right-from-square text-xs" aria-hidden="true"></i>
            </a>
          </div>
        </section>

        {/* Galleri */}
        <div className="my-16">
          <GalleryLightbox
            galleryKey="bygningsaffald"
            title="Billeder fra pladsen og materialer"
            description="Genbrugsmaterialer, knust beton, grus, sand og modtageplads på Industrivej 3 i Frederiksværk."
          />
        </div>

        {/* Formular */}
        <div className="mt-16">
          <WebContactForm
            title="Forespørgsel på materialer eller modtagelse"
            subtitle="Udfyld formularen for at forhøre dig om køb af materialer eller modtagelse af rene materialer."
            submitText="Send forespørgsel"
            showAddress={false}
            showTaskTypeSelect={false}
            customFields={
              <div className="space-y-4">
                <div>
                  <label htmlFor="action_type" className="block text-xs font-heading font-bold uppercase text-stone-700 mb-1">
                    Forespørgslen drejer sig om <span className="text-[#0072c0]">*</span>
                  </label>
                  <select
                    id="action_type"
                    name="action_type"
                    required
                    className="w-full px-3.5 py-2.5 bg-stone-50 border border-stone-300 text-stone-900 text-sm focus:bg-white focus:border-[#0072c0] focus:ring-1 focus:ring-[#0072c0] focus:outline-none transition-colors"
                  >
                    <option value="Køb og afhentning af materialer">Køb og afhentning af materialer</option>
                    <option value="Modtagelse af rene bygge- og anlægsmaterialer">Modtagelse af rene bygge- og anlægsmaterialer</option>
                    <option value="Priser og lagerstatus">Priser og lagerstatus</option>
                    <option value="Andet">Andet</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="material_type" className="block text-xs font-heading font-bold uppercase text-stone-700 mb-1">
                    Angiv gerne materialetype og estimeret mængde
                  </label>
                  <input
                    id="material_type"
                    name="material_type"
                    type="text"
                    placeholder="F.eks. Stabilgrus 0/32 eller Ren beton"
                    className="w-full px-3.5 py-2.5 bg-stone-50 border border-stone-300 text-stone-900 text-sm focus:bg-white focus:border-[#0072c0] focus:ring-1 focus:ring-[#0072c0] focus:outline-none transition-colors"
                  />
                </div>
              </div>
            }
            showFileUpload={true}
            fileLabel="Vedhæft gerne foto af materialer (valgfrit)"
          />
        </div>

        {/* Lightbox for Materialer og pladsen */}
        <ImageLightboxModal
          isOpen={lightboxState.isOpen}
          onClose={() => setLightboxState({ ...lightboxState, isOpen: false })}
          images={materialImages}
          currentIndex={lightboxState.index}
          onNavigate={(newIdx) => setLightboxState({ ...lightboxState, index: newIdx })}
          title="På pladsen – materialer og genbrug"
        />

      </div>
    </div>
  );
}
